import React, { useRef } from 'react';
import { Box, Flex, Text, Button, Icon, HStack, VStack } from '@chakra-ui/react';
import { Outlet, useNavigate, useLocation, useOutlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGPS } from '../context/GPSContext';
import { FiLogOut, FiMenu, FiChevronLeft, FiNavigation } from 'react-icons/fi';
import { IconButton, Image } from '@chakra-ui/react';
import MobileBottomNav from '../components/MobileBottomNav';
import NotificationBell from '../components/NotificationBell';
import useKeyboardViewport from '../hooks/useKeyboardViewport';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/logo.png';
import ParentDashboard from '../pages/ParentDashboard';
import ParentAnnouncementsPage from '../pages/ParentAnnouncementsPage';
import ParentChatListPage from '../pages/ParentChatListPage';

const ParentLayout = () => {
    // Logout logic moved to Hamburger Menu (Drawer) or Settings page
    const location = useLocation();
    const navigate = useNavigate();
    const element = useOutlet();
    const { isTracking, gpsStatus } = useGPS();
    const { user } = useAuth();
    const shellRef = useRef(null);

    // 【2026-08-13】外殼原本是 fixed + bottom:0，完全不理會鍵盤：iOS 的 layout viewport
    // 開鍵盤時不會變矮，所以外殼維持全高、輸入列被鍵盤蓋住，WebView 只好把整頁往上捲一格
    // —— user 實機回報「APP上的聯絡簿 鍵盤瞬間跳出 太突兀」就是這一格。
    // 交給 visualViewport 之後外殼會跟著鍵盤縮放，並帶 250ms 的同曲線 transition，
    // 變成鍵盤從下方滑入、對話一起等量上移（見 useKeyboardViewport 的完整說明）。
    useKeyboardViewport(shellRef);

    // [A2] 家長端 header 顯示機構品牌名稱：取第一個孩子的機構（多數家庭只有一個機構）。
    // display_name 未設定（天籟現況）時 fallback 用機構原本的 name；完全沒有孩子資料時
    // （尚未載入 / 帳號還沒綁定孩子）不顯示，維持現行「只有 logo」畫面，零影響。
    const primaryInstitution = user?.children?.[0]?.institution || null;
    const institutionDisplayName = primaryInstitution
        ? (primaryInstitution.display_name || primaryInstitution.name)
        : null;

    // ── 導航層級模型 ──
    // 「公告列表 → 詳情」與「對話列表 → 對話室」都是兩層（詳情/房間疊在列表上，返回露出列表，
    // 再返回才回首頁）；[B3] 對話室沿用同一個 fromList 標記機制，別跟公告的 twoLevel 混淆命名。
    // 其餘頁面（含 banner 進的詳情、聯絡簿、導航列切換）一律單層，返回回首頁。
    const path = location.pathname;
    const isDashboard = path === '/parent/dashboard';
    const isAnnList = path === '/parent/announcements';
    const isAnnDetail = /^\/parent\/announcements\/[^/]+$/.test(path);
    const isChatList = path === '/parent/chat';
    const isChatRoom = /^\/parent\/chat\/[^/]+$/.test(path);
    const fromList = !!location.state?.fromList;        // 由列表點進詳情/房間時帶的標記（banner、深連結不帶）
    const twoLevel = isAnnDetail && fromList;            // 兩層：公告詳情疊在列表上
    const twoLevelChat = isChatRoom && fromList;         // 兩層：[B3] 對話室疊在對話列表上
    const annSection = isAnnList || twoLevel;            // 公告區（底層固定渲染列表，不重新掛載）
    const chatSection = isChatList || twoLevelChat;      // [B3] 對話區（底層固定渲染列表，不重新掛載）
    const primaryKey = annSection ? '/parent/announcements' : (chatSection ? '/parent/chat' : path);  // 底層 key 穩定 → 詳情/房間疊上時列表不動畫
    const layer2Exit = isDashboard ? 'slideOut' : 'fade'; // 回首頁→滑出；橫向切換→淡出(避免兩頁交叉閃爍)

    const overlayMotionStyle = {
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        // 頁面滑入層的底色＝App 畫布（黃）。用 gray-50（凹槽米色）會讓每個頁面
        // 底層變米色，只有卡片之外的空白處看得出來，反而更怪。
        backgroundColor: 'var(--chakra-colors-canvas)', overflowY: 'auto',
        WebkitOverflowScrolling: 'touch', willChange: 'scroll-position', transform: 'translateZ(0)',
    };
    const overlayInnerPb = path === '/parent/settings' ? '0' : 'calc(env(safe-area-inset-bottom) + 90px)';
    // [B3] 對話室要自己管內部捲動（訊息區捲動、輸入列釘在底部），不能用其餘頁面共用的
    // 「外層一路捲、pb 留白閃過底部導航」模式：改成固定高度的 flex 欄，pb=0（底部導航本來就會
    // 在對話室隱藏，見下方 MobileBottomNav 那行），讓房間元件自己的 h="100%" 有個有限高度可撐。
    const chatRoomBoxProps = {
        pt: 'calc(env(safe-area-inset-top) + 60px + 10px)',
        h: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', px: 0,
    };
    const defaultBoxProps = {
        pt: 'calc(env(safe-area-inset-top) + 60px + 10px)', pb: overlayInnerPb, minH: '100%', px: 0,
    };


    const getTitle = (pathname) => {
        if (pathname.includes('/pickup')) return '孩子接送';
        if (pathname.includes('/communication-book')) return '電子聯絡簿';
        // [2026-08-06] 由「親師對話」改為「聯絡簿」：家長是從底部導航標示為「聯絡簿」的入口
        // （MobileBottomNav.jsx:129，chat 開通後導向 /parent/chat）進來的，header 卻顯示另一個
        // 名字會讓人以為點錯頁。對家長而言這頁的心智模型就是聯絡簿，只是多了可以直接回訊息的能力。
        if (pathname.includes('/chat')) return '聯絡簿';
        if (pathname.includes('/announcements')) return '校園公告';
        if (pathname.includes('/leaves')) return '請假登記';
        if (pathname.includes('/lunch')) return '訂餐登記';
        if (pathname.includes('/settings')) return '基本資料';
        return '校園接送'; // Default
    };

    const title = getTitle(location.pathname);

    return (
        <Box
            ref={shellRef}
            position="fixed" // Changed from h="100vh" to fixed inset to prevent address bar jitter
            top={0}
            left={0}
            right={0}
            bottom={0}   // 沒有 visualViewport 時的全高退路；hook 會改寫 top+height（兩者同時指定時 bottom 依 CSS 規則自動失效）
            w="100vw"
            // 外殼＝App 畫布（黃）。gray.50 在新配色是「凹槽/狀態卡」語意（米色），
            // 拿它當外殼會讓整個 App 底變米色，與參考設計不符。
            bg="canvas"
            overflow="hidden" // Lock the outer container
            sx={{
                // iOS Safe Area Support
                paddingTop: 'env(safe-area-inset-top)',
                paddingBottom: 'env(safe-area-inset-bottom)',
                // Prevent elastic bouncing of the whole app
                overscrollBehavior: 'none',
                // 見 AdminLayout 同名變數的說明：共用頁靠這三個變數在兩種底色下都達標
                '--fg-weak': 'var(--chakra-colors-onCanvas-300)',
                '--fg-muted': 'var(--chakra-colors-onCanvas-400)',
                '--fg-body': 'var(--chakra-colors-onCanvas-500)',
            }}
        >
            {/* Header: Fixed Top with Nested Structure */}
            <Box
                as="header"
                position="fixed"
                top={0}
                left={0}
                right={0}
                // 同老師端：header 用畫布色而非卡片色，否則會在「黃安全區 → 米白 header
                // → 黃內容」之間形成一條突兀的橫帶。陰影也一併拿掉，外框保持連續。
                bg="canvas"
                zIndex={1000}
            >
                {/* 1. Safe Area Spacer (The Notch) */}
                <Box h="env(safe-area-inset-top)" w="100%" />

                {/* 2. Toolbar Content (Exactly 60px) */}
                <Flex
                    h="60px"
                    align="center"
                    justify="space-between"
                    px={4}
                    position="relative" // For absolute centering of logo
                >
                    {/* Left: Hamburger (Menu) or Back */}
                    {location.pathname === '/parent/dashboard' ? (
                        <Box w={10} />
                    ) : (
                        <IconButton
                            icon={<Icon as={FiChevronLeft} boxSize={7} />}
                            variant="ghost"
                            aria-label="Back"
                            color="onCanvas.400"
                            onClick={() => {
                                // 兩層（公告列表→詳情／[B3] 對話列表→對話室）：返回先回列表；其餘一律回首頁
                                if (twoLevel) navigate('/parent/announcements');
                                else if (twoLevelChat) navigate('/parent/chat');
                                else navigate('/parent/dashboard');
                            }}
                        />
                    )}

                    {/* Center: Logo or Title */}
                    <Box
                        position="absolute"
                        left={0}
                        right={0}
                        top={0}
                        bottom={0}
                        pointerEvents="none"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        {location.pathname === '/parent/dashboard' ? (
                            <VStack spacing={0} pointerEvents="auto">
                                <Image src={logo} alt="Natural Education" h="44px" objectFit="contain" />
                                {institutionDisplayName && (
                                    <Text fontSize="11px" color="onCanvas.400" fontWeight="medium" noOfLines={1} maxW="200px">
                                        {institutionDisplayName}
                                    </Text>
                                )}
                            </VStack>
                        ) : (
                            <Text fontSize="lg" fontWeight="bold" color="onCanvas.500">
                                {title}
                            </Text>
                        )}
                    </Box>

                    {/* Right: GPS Status & Notification */}
                    <HStack spacing={2} zIndex={2}>
                        {isTracking && (
                            <Flex
                                align="center"
                                bg={gpsStatus === 'CONNECTED' ? "red.500" : "orange.400"}
                                color="white"
                                px={2}
                                py={1}
                                borderRadius="full"
                                fontSize="xs"
                                fontWeight="bold"
                                className="blink"
                                onClick={() => navigate('/pickup')}
                                cursor="pointer"
                            >
                                <Icon as={FiNavigation} mr={1} boxSize={3} />
                                <Text>GPS</Text>
                            </Flex>
                        )}

                        <NotificationBell />
                    </HStack>
                </Flex>
            </Box>

            {/* Main Content: Stacking Architecture */}
            <Box position="relative" w="100%" h="100%" bg="canvas" overflow="hidden">
                {/* Layer 1: Persistent Dashboard (Base) */}
                <Box
                    position="absolute"
                    top={0}
                    left={0}
                    w="100%"
                    h="100%"
                    overflowY="auto"
                    // Dynamic padding: Safe Area + Header(60) + Tiny Buffer(10)
                    pt="calc(env(safe-area-inset-top) + 60px + 10px)"
                    // Bottom: Safe Area + Nav(approx 70-80)
                    pb="calc(env(safe-area-inset-bottom) + 80px)"
                    px={4}
                    zIndex={1}
                    sx={{
                        // Enable iOS Momentum Scrolling (Critical)
                        WebkitOverflowScrolling: 'touch',
                        // GPU Acceleration for smoother scroll
                        willChange: 'scroll-position',
                        transform: 'translateZ(0)',
                    }}
                >
                    <ParentDashboard />
                </Box>

                {/* Layer 2: 主覆蓋頁（非首頁）。公告區時固定渲染「列表」墊在底層（詳情疊上時不重新動畫） */}
                <AnimatePresence custom={layer2Exit}>
                    {!isDashboard && (
                        <motion.div
                            key={primaryKey}
                            custom={layer2Exit}
                            variants={{
                                initial: { x: '100%' },
                                animate: { x: 0 },
                                exit: (mode) => (mode === 'slideOut' ? { x: '100%' } : { opacity: 0 }),
                            }}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            style={{ ...overlayMotionStyle, zIndex: 10 }}
                        >
                            <Box {...(isChatRoom && !twoLevelChat ? chatRoomBoxProps : defaultBoxProps)}>
                                {annSection ? <ParentAnnouncementsPage /> : chatSection ? <ParentChatListPage /> : <Outlet />}
                            </Box>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Layer 3: 公告詳情／[B3] 對話室（從列表進）→ 疊在列表上滑入；返回滑出露出列表 */}
                <AnimatePresence>
                    {(twoLevel || twoLevelChat) && (
                        <motion.div
                            key={twoLevel ? 'ann-detail' : 'chat-room'}
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            style={{ ...overlayMotionStyle, zIndex: 20 }}
                        >
                            <Box {...(twoLevelChat ? chatRoomBoxProps : defaultBoxProps)}>
                                <Outlet />
                            </Box>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Box>

            {/* Bottom Nav: Floating Capsule (常駐：除了基本資料/設定頁，其餘頁面都顯示)
                [B3] 對話室額外隱藏——房間自己在最底部釘了輸入列，浮動導航膠囊會跟它疊在一起。 */}
            {location.pathname !== '/parent/settings' && !isChatRoom && <MobileBottomNav variant="floating" />}
        </Box>
    );
};

export default ParentLayout;
